import React from "react";
import { fmtDate, money, flattenEtapes } from "../lib/helpers";
import * as Icons from "lucide-react";

export const dueDate = (p) => {
  const ds = flattenEtapes(p).map((et) => et.planned_date).filter(Boolean).sort();
  return ds.length ? ds[ds.length - 1] : null;
};
export const isDone = (p) => p.progress === 100;
export const isOverdue = (p) => {
  const d = dueDate(p);
  if (isDone(p) || !d) return false;
  const t = new Date(); t.setHours(0, 0, 0, 0);
  return new Date(d) < t;
};
const MONTHS = ["", "Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];

export const ProjectCard = ({ project: p, clientName, onOpen, onDuplicate, onDuplicateNextMonth, onEdit, onDelete }) => {
  const done = isDone(p);
  const due = dueDate(p);
  const team = p.team || [];
  return (
    <div data-testid={`admin-project-${p.id}`}
      className={`relative text-left bg-sable rounded-sm border border-marine/10 shadow-sm p-5 card-lift hover:border-marine/40 flex flex-col ${done ? "opacity-70" : ""}`}>
      {/* Boutons d'action : plus serrés sur téléphone pour rendre de la largeur à l'étiquette de module (lot 7, 29/09). */}
      <div className="absolute top-3 right-3 z-10 flex gap-0.5 sm:gap-1">
        {onEdit && (
          <button onClick={(e) => { e.stopPropagation(); onEdit(p); }} data-testid={`edit-project-${p.id}`}
            title="Modifier le projet"
            className="p-1 sm:p-1.5 rounded-sm text-marine hover:opacity-70 hover:bg-marine-50 transition-colors">
            <Icons.Pencil size={16} />
          </button>
        )}
        {onDuplicateNextMonth && (
          <button onClick={(e) => { e.stopPropagation(); onDuplicateNextMonth(p); }} data-testid={`duplicate-next-month-${p.id}`}
            title="Dupliquer pour le mois suivant"
            className="p-1 sm:p-1.5 rounded-sm text-marine hover:text-bordeaux hover:bg-bordeaux-50 transition-colors">
            <Icons.CalendarPlus size={16} />
          </button>
        )}
        {onDuplicate && (
          <button onClick={(e) => { e.stopPropagation(); onDuplicate(p); }} data-testid={`duplicate-project-${p.id}`}
            title="Dupliquer ce projet"
            className="p-1 sm:p-1.5 rounded-sm text-marine hover:opacity-70 hover:bg-marine-50 transition-colors">
            <Icons.Copy size={16} />
          </button>
        )}
        {onDelete && (
          <button onClick={(e) => { e.stopPropagation(); onDelete(p); }} data-testid={`delete-project-${p.id}`}
            title="Supprimer le projet"
            className="p-1 sm:p-1.5 rounded-sm text-marine hover:text-rouge hover:bg-rouge-50 transition-colors">
            <Icons.Trash2 size={16} />
          </button>
        )}
      </div>
      <button onClick={() => onOpen(p)} className="text-left flex flex-col flex-1 w-full">
        <div className="flex items-start justify-between gap-2 mb-3 pr-24 sm:pr-28">
          <div className="text-xs uppercase tracking-wider text-bordeaux font-semibold">{p.type_label}</div>
          <span data-testid={`project-status-${p.id}`} className={`shrink-0 whitespace-nowrap text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-sm font-medium ${done ? "bg-bleu-50 border border-bleu/30 text-bleu" : "bg-marine-50 text-marine"}`}>
            {done ? "Finalisé" : "En cours"}
          </span>
        </div>
        <div className="font-display text-lg text-marine leading-tight mb-1 flex items-center gap-2 flex-wrap">
          {p.name}
          {p.draft && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-sable border border-marine/30 text-gris-texte">Brouillon</span>}
          {p.recurring && <span data-testid={`project-recurring-${p.id}`} className="text-[9px] px-1.5 py-0.5 rounded-sm bg-bordeaux-50 text-bordeaux border border-bordeaux/30 inline-flex items-center gap-1">Abonnement</span>}
          {p.period_month && p.period_year && <span className="text-[9px] px-1.5 py-0.5 rounded-sm bg-marine-50 text-marine border border-marine/30">{MONTHS[p.period_month]} {p.period_year}</span>}
        </div>
        <div className="text-sm text-marine/80 flex items-center gap-1.5 mb-4"><Icons.Building2 size={16} className="text-marine" /> {clientName(p.client_id)}</div>
        <div className="mt-auto space-y-2.5 pt-3 border-t border-marine/10 w-full">
          <div className="flex items-center gap-2 text-xs text-marine/80">
            <Icons.Users size={16} className="text-marine shrink-0" />
            {team.length === 0 ? <span className="italic">Équipe non assignée</span> : <span className="truncate">{team.map((t) => t.name).join(", ")}</span>}
          </div>
          <div className="flex items-center gap-2 text-xs">
            <Icons.CalendarClock size={16} className={`shrink-0 ${isOverdue(p) ? "text-rouge" : done ? "text-marine" : "text-bordeaux"}`} />
            <span className={isOverdue(p) ? "text-rouge-texte font-medium" : done ? "text-marine/80" : "text-marine font-medium"}>
              {due ? `Rendu : ${fmtDate(due)}` : "Rendu : à planifier"}
            </span>
            {isOverdue(p) && <span data-testid={`project-overdue-${p.id}`} className="ml-auto inline-flex items-center gap-1 bg-rouge-50 text-rouge border border-rouge/40 px-1.5 py-0.5 rounded-sm text-[9px] uppercase tracking-wider">En retard</span>}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1.5 bg-sable border border-marine/30 rounded-full overflow-hidden"><div className="h-full bg-marine" style={{ width: `${p.progress}%` }} /></div>
            <span className="text-xs text-marine font-medium">{p.progress}%</span>
          </div>
          {p.financials && p.financials.total > 0 && (
            <div className="flex items-center gap-2 text-xs pt-0.5" data-testid={`project-billing-${p.id}`}>
              <Icons.Wallet size={16} className={`shrink-0 ${p.overdue_amount > 0 ? "text-rouge" : p.financials.reste_a_encaisser > 0 ? "text-bordeaux" : "text-bleu"}`} />
              {p.financials.reste_a_encaisser > 0
                ? <span className={p.overdue_amount > 0 ? "text-rouge font-medium" : "text-marine font-medium"}>Reste à encaisser : {money(p.financials.reste_a_encaisser, p.currency)}</span>
                : <span className="text-bleu font-medium">Soldé · {money(p.financials.total, p.currency)}</span>}
            </div>
          )}
        </div>
      </button>
    </div>
  );
};
